// Authorized.jsx (componente principal)
import React, {
  useEffect,
  useState,
  useMemo,
  createContext,
  useContext,
} from "react";
import { Layout, Button, Select } from "antd";
import { ArrowLeftOutlined, SettingOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import CustomModal from "../../../components/modal/CustomModal.jsx";
import ConfirmationModal from "../../../components/modal/ConfirmationModal.jsx";
import { useTheme } from "../../../contexts/ThemeContext.jsx";
import { useAuth } from "../../../hooks/auth.jsx";
import { defaultRoutes } from "../../../routes/routes.jsx";
import { filterRoutesByRoles } from "../../../helpers/permissions.js";

import SimpleSider from "../../../components/sidebar/sider/SimpleSider.jsx";
import { useReportBuilder } from "../../../contexts/ReportBuilderContext.jsx";
import SynchronizationSettings from "../../../components/synchronizations/SynchronizationSettings.jsx";
import SynchronizationService from "../../../services/synchronizationService.js";
import useProjects from "../../../hooks/useProjects.js";
import ProjectSettings from "../../../components/projects/ProjectSettings.jsx";
import ProjectService from "../../../services/projectService";
import ProjectsSection from "../../../components/card/ProjectCard/ProjectsSection.jsx";
import Toast from "../../../components/ui/toast";
import AntdTourToggle from "../../../components/usability/AntdTourToggle.jsx";
import OnboardingManager from "../../../components/usability/OnboardingManager.jsx";
import LoginUpdateNoticeModal from "../../../components/modal/LoginUpdateNoticeModal.jsx";
import { useLoginUpdateNotice } from "../../../hooks/useLoginUpdateNotice.js";
import { NotificationsProvider } from "../../../contexts/NotificationsContext.jsx";
import NotificationsDrawer from "../../../components/notifications/NotificationsDrawer.jsx";
import { getSafeExternalUrl } from "../../../utils/safeExternalUrl.js";

// Context para controlar o label da página no header
const PageLabelContext = createContext();

export const usePageLabel = () => {
  const context = useContext(PageLabelContext);
  if (!context) {
    throw new Error(
      "usePageLabel deve ser usado dentro de um PageLabelProvider",
    );
  }
  return context;
};

// Context para controlar o botão de navegação no header
const NavigationButtonContext = createContext();

export const useNavigationButton = () => {
  const context = useContext(NavigationButtonContext);
  return context || { setNavigationButton: () => {} };
};

// Contexto para controlar o projeto selecionado globalmente
const SelectedProjectContext = createContext();

export const useSelectedProject = () => {
  const context = useContext(SelectedProjectContext);
  if (!context) {
    throw new Error(
      "useSelectedProject deve ser usado dentro de um SelectedProjectProvider",
    );
  }
  return context;
};

const { Content } = Layout;

const Authorized = ({ children, userName }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const { theme } = useTheme();
  const { user, getTenantInfo, canAccessGestaoMenu, hasEffectiveModule, canAccessAcademy, canAccessPlanos, canAccessFechamento } = useAuth();
  const { sync, isDirty, isPreview, resetState, title, setSync } = useReportBuilder();
  const [unsavedLeaveTarget, setUnsavedLeaveTarget] = useState(null);
  const [unsavedModalOpen, setUnsavedModalOpen] = useState(false);
  const { projects, loadProjects } = useProjects();
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [savingNewProject, setSavingNewProject] = useState(false);
  const [newProjectDraft, setNewProjectDraft] = useState({ isNew: true });
  const [collapsed, setCollapsed] = useState(false);
  const {
    notice: loginUpdateNotice,
    open: loginUpdateNoticeOpen,
    checkCurrentNotice,
    closeNotice: closeLoginUpdateNotice,
    interactionId: loginUpdateNoticeInteractionId,
  } = useLoginUpdateNotice();

  const handleOpenProjectSettings = (project) => {
    setNewProjectDraft({ ...project, isNew: false });
    setCreateProjectModalOpen(true);
  };

  const handleEditSelectedProject = (projectId) => {
    const id = projectId ?? selectedProjectId;
    if (!id || id === "__all__") return;
    const project = projects?.find((p) => p.id === id);
    if (project) {
      handleOpenProjectSettings(project);
    }
  };

  const handleSaveProjectSettings = async (updatedProject) => {
    try {
      setSavingNewProject(true);
      const backendData = ProjectService.mapToBackend({
        ...updatedProject,
        isNew: false,
      });
      const response = await ProjectService.upsert(backendData);
      const saved = response?.data
        ? ProjectService.mapToFrontend(response.data)
        : response;
      if (saved?.id) {
        setCreateProjectModalOpen(false);
        setSelectedProjectId(saved.id);
        const params = new URLSearchParams(location.search);
        params.set("projectId", saved.id);
        navigate({
          pathname: location.pathname,
          search: `?${params.toString()}`,
        });
      } else {
        setCreateProjectModalOpen(false);
      }
    } catch (e) {
      setCreateProjectModalOpen(false);
    } finally {
      setSavingNewProject(false);
    }
  };

  const tenantInfo = getTenantInfo();
  const tenantIdentificador = tenantInfo?.identificador;
  const isBackofficeTenant = tenantIdentificador === "d4cbackoffice";

  // Filtrar rotas baseado nas roles do usuário e tenant
  const filteredRoutes = useMemo(() => {
    if (!user) return [];

    let routes = filterRoutesByRoles(defaultRoutes, user);

    if (isBackofficeTenant) {
      const backofficeRoute = defaultRoutes.find(
        (route) => route.key === "/back-office",
      );

      if (backofficeRoute && !routes.some((route) => route.key === "/back-office")) {
        routes = [...routes, backofficeRoute];
      }
    }

    if (!isBackofficeTenant) {
      routes = routes.filter(
        (route) => route.key !== "/back-office" && route.key !== "/auditoria",
      );
    }

    if (isBackofficeTenant) {
      routes = routes.map((route) =>
        route.key === "/back-office" ? { ...route, hidden: false } : route,
      );
    }

    routes = routes.filter((route) => {
      const required = route?.requiredModules;
      if (!Array.isArray(required) || required.length === 0) return true;
      return required.some((m) => hasEffectiveModule?.(m));
    });

    routes = routes.filter((route) => {
      const model = route?.gestaoModel;
      if (!model) return true;
      return canAccessGestaoMenu?.(model);
    });

    routes = routes.filter((route) => {
      if (route?.key !== "/academy") return true;
      return typeof canAccessAcademy === "function" ? canAccessAcademy() : true;
    });

    routes = routes.filter((route) => {
      if (route?.key !== "/billing") return true;
      return typeof canAccessPlanos === "function" ? canAccessPlanos() : true;
    });

    const fechamentoByKey = new Set(["/fechamentos", "/fechamentos/novo", "/fechamentos/:id"]);
    routes = routes.filter((route) => {
      const requiredMods = route?.requiredModules;
      const isFechamento =
        fechamentoByKey.has(route?.key) ||
        (Array.isArray(requiredMods) && requiredMods.includes("fechamentos") && route?.key !== "/billing");
      if (!isFechamento) return true;
      return typeof canAccessFechamento === "function" ? canAccessFechamento() : true;
    });

    const sectionKeysWithItems = new Set(
      routes
        .filter((r) => r?.sectionKey)
        .map((r) => r.sectionKey),
    );
    routes = routes.filter((route) => {
      if (route?.isSectionHeader !== true) return true;
      return sectionKeysWithItems.has(route.key);
    });

    return routes;
  }, [canAccessAcademy, canAccessPlanos, canAccessFechamento, canAccessGestaoMenu, hasEffectiveModule, user, isBackofficeTenant]);

  // Calcular o label da página atual
  const currentPageLabel = useMemo(() => {
    if (!location.pathname) return "Usuários e Permissões";

    const findRouteByPath = (routes, pathname) => {
      for (const route of routes || []) {
        if (!route) continue;
        if (pathname === route.key) return route;
        if (pathname === "/" && route.key === "/") return route;
        if (route.children?.length) {
          const found = findRouteByPath(route.children, pathname);
          if (found) return found;
        }
      }
      return null;
    };

    const currentRoute = findRouteByPath(defaultRoutes, location.pathname);

    if (currentRoute && currentRoute.label) {
      // Extrair o texto do label (que pode ser um JSX element)
      if (typeof currentRoute.label === "object" && currentRoute.label.props) {
        return currentRoute.label.props.children;
      }
      return currentRoute.label;
    }

    // Fallback baseado no pathname
    const pathMap = {
      "/": "Usuários e Permissões",
      "/projects": "Projetos",
      "/synchronizations": "Sincronizações ERP",
      "/reports": "Relatórios",
      "/reports/sync-selector": "Escolher Fonte de Dados",
      "/reports/builder": "Builder de Relatórios",
      "/permissions": "Administração de Permissões",
      "/permissions-demo": "Demo de Permissões",
      "/form": "Formulário - Exemplo",
      "/formbuilder": "Gerador de Form",
      "/table": "Crud - Exemplo",
      "/modal": "Modal - Exemplo",
      "/charts": "Charts - Exemplo",
      "/theme-tester": "Testador de Whitelabel",
      "/whitelabel-demo": "Demo Whitelabel",
      "/whitelabel-example": "Exemplo Whitelabel",
      "/whitelabel-guide": "Guia Whitelabel",
      "/auditoria": "Auditoria",
      "/back-office": "BackOffice",
    };

    // Verificar padrões de rotas dinâmicas
    if (location.pathname.startsWith("/reports/sync-selector/")) {
      return "Escolher Fonte de Dados";
    }

    return pathMap[location.pathname] || "Data4Company";
  }, [location.pathname]);

  // Função para permitir que páginas filhas atualizem o label do header
  const [customPageLabel, setCustomPageLabel] = useState(null);

  // Label final (custom tem prioridade sobre o calculado)
  const finalPageLabel = customPageLabel || currentPageLabel;

  // Estado para o botão de navegação no header
  const [navigationButton, setNavigationButton] = useState(null);

  // Função para determinar o botão baseado na rota atual
  const getNavigationButton = (pathname) => {
    // Regex para detectar rotas de projetos
    const projectSettingsMatch = pathname.match(
      /^\/projects\/([^\/]+)\/settings$/,
    );
    const projectIntegrationsMatch = pathname.match(
      /^\/projects\/([^\/]+)\/integrations$/,
    );
    const projectIntegrationsPageMatch = pathname.match(
      /^\/projects\/([^\/]+)\/integrations\/([^\/]+)$/,
    );

    // Regex para detectar rotas de relatórios
    const reportSyncSelectorMatch = pathname.match(
      /^\/reports\/sync-selector$/,
    );
    const reportSyncSelectorWithProjectMatch = pathname.match(
      /^\/reports\/sync-selector\/(.+)$/,
    );

    // NOTA: Builder logic removida daqui pois agora é tratada no ReportHeader

    if (reportSyncSelectorMatch) {
      return (
        <Button
          onClick={() => navigate("/reports")}
          style={{
            background: theme.background,
            border: "none",
            color: theme.primary,
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "14px",
          }}
        >
          <ArrowLeftOutlined /> Voltar para relatórios
        </Button>
      );
    }

    if (reportSyncSelectorWithProjectMatch) {
      const projectId = reportSyncSelectorWithProjectMatch[1];
      return (
        <Button
          onClick={() => navigate(`/reports?projectId=${projectId}`)}
          style={{
            background: theme.background,
            border: "none",
            color: theme.primary,
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "14px",
          }}
        >
          <ArrowLeftOutlined /> Voltar para relatórios
        </Button>
      );
    }

    if (projectIntegrationsMatch) {
      const projectId = projectIntegrationsMatch[1];
      return (
        <Button
          onClick={() => navigate(`/projects/${projectId}/settings`)}
          style={{
            background: theme.background,
            border: "none",
            color: theme.primary,
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "14px",
          }}
        >
          <ArrowLeftOutlined /> Voltar para configurações
        </Button>
      );
    }
    if (projectIntegrationsPageMatch) {
      return (
        <Button
          onClick={() => navigate("/projects")}
          style={{
            background: theme.background,
            border: "none",
            color: theme.primary,
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "14px",
          }}
        >
          <ArrowLeftOutlined /> Voltar para projetos
        </Button>
      );
    }

    if (projectSettingsMatch) {
      return (
        <Button
          onClick={() => navigate("/projects")}
          style={{
            background: theme.background,
            border: "none",
            color: theme.primary,
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: "14px",
          }}
        >
          <ArrowLeftOutlined /> Voltar para projetos
        </Button>
      );
    }

    return null;
  };

  useEffect(() => {
    const matchesPath = (routeKey, pathname) => {
      if (!routeKey || !pathname) return false;
      if (pathname === routeKey) return true;
      return pathname.startsWith(`${routeKey}/`);
    };

    const findTopOpenKey = (routes, pathname) => {
      for (const r of routes || []) {
        if (!r?.children?.length) continue;
        if (matchesPath(r.key, pathname)) return r.key;
        const stack = [...r.children];
        while (stack.length) {
          const curr = stack.pop();
          if (matchesPath(curr?.key, pathname)) return r.key;
          if (curr?.children?.length) stack.push(...curr.children);
        }
      }
      return null;
    };

    const nextOpen = findTopOpenKey(filteredRoutes, location.pathname);
    setOpenKeys(nextOpen ? [nextOpen] : []);

    // Limpar custom label quando mudamos de rota
    setCustomPageLabel(null);

    // Configurar botão de navegação automaticamente baseado na rota
    const autoNavigationButton = getNavigationButton(location.pathname);
    setNavigationButton(autoNavigationButton);

    if (location.pathname.startsWith("/reports/advanced")) {
      const params = new URLSearchParams(location.search);
      const current = params.get("projectId");
      const desired = selectedProjectId ? String(selectedProjectId) : null;
      const shouldUpdate = desired ? current !== desired : current !== null;
      if (shouldUpdate) {
        if (desired) {
          params.set("projectId", desired);
        } else {
          params.delete("projectId");
        }
        navigate({
          pathname: location.pathname,
          search: `?${params.toString()}`,
        });
      }
    }
  }, [location, filteredRoutes, navigate, theme, isDirty, selectedProjectId]);

  const showProjectTopbar = useMemo(() => {
    if (location.pathname.startsWith("/portfolio/configurator")) return true;
    if (!location.pathname.startsWith("/reports/advanced")) return false;
    const params = new URLSearchParams(location.search || "");
    const tab = params.get("tab") || "reports";
    return tab === "reports" || tab === "portfolios";
  }, [location.pathname, location.search]);

  const projectSelectOptions = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    return [
      { label: "Todos os projetos", value: "__all__" },
      ...list.map((p) => ({ label: p?.name ?? "", value: p?.id })),
    ].filter((o) => String(o.label || "").trim().length > 0 && o.value != null);
  }, [projects]);

  useEffect(() => {
    const value = selectedProjectId ?? "__all__";
    window.localStorage.setItem("selectedProjectId", String(value));
  }, [selectedProjectId]);

  useEffect(() => {
    if (!user) return;
    // Deep-links que já abrem um modal específico sozinhos (ex.: "Revisar contas ignoradas" do
    // wizard de Fechamento, que abre numa aba nova direto no modal de Contexto do fechamento) não
    // devem competir com a campanha de novidades — os dois some empilhados sem nenhum precisar
    // fechar, já que o de novidades não é fechável clicando fora. Só marca a checagem como feita
    // (para não disparar de novo se a aba continuar aberta e navegar para outra rota depois).
    if (new URLSearchParams(location.search).has("openClosingContext")) {
      sessionStorage.setItem("loginUpdateNoticeInitialCheckDone", "true");
      return;
    }
    const shouldShowNotice = sessionStorage.getItem("showLoginUpdateNotice");
    if (shouldShowNotice === "true") {
      sessionStorage.removeItem("showLoginUpdateNotice");
      sessionStorage.setItem("loginUpdateNoticeInitialCheckDone", "true");
      checkCurrentNotice();
    } else if (!sessionStorage.getItem("loginUpdateNoticeInitialCheckDone")) {
      // Usuário já estava logado (ex: nova aba ou reload): verifica campanha uma única vez por sessão
      sessionStorage.setItem("loginUpdateNoticeInitialCheckDone", "true");
      checkCurrentNotice();
    }
  }, [user, checkCurrentNotice, location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pid = params.get("projectId");
    if (pid) {
      setSelectedProjectId(pid);
    } else {
      const stored = window.localStorage.getItem("selectedProjectId");
      if (selectedProjectId === null && stored) {
        const value = stored === "__all__" ? null : stored;
        setSelectedProjectId(value);
      }
    }
  }, [location.search]);

  // Eventos para tours de Projetos
  useEffect(() => {
    const openViewProjects = () => navigate("/company?tab=projects");
    const openCreateProject = () => {
      setNewProjectDraft({ isNew: true });
      setCreateProjectModalOpen(true);
    };
    const closeCreateProject = () => setCreateProjectModalOpen(false);
    const closeViewProjects = () => {};
    window.addEventListener("tour-open-view-projects", openViewProjects);
    window.addEventListener("tour-open-create-project", openCreateProject);
    window.addEventListener("tour-close-create-project", closeCreateProject);
    window.addEventListener("tour-close-view-projects", closeViewProjects);
    return () => {
      window.removeEventListener("tour-open-view-projects", openViewProjects);
      window.removeEventListener("tour-open-create-project", openCreateProject);
      window.removeEventListener(
        "tour-close-create-project",
        closeCreateProject,
      );
      window.removeEventListener("tour-close-view-projects", closeViewProjects);
    };
  }, [navigate]);

  useEffect(() => {
    if (selectedProjectId === null) {
      return;
    }
    if (!selectedProjectId && projects && projects.length > 0) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  const handleProjectChange = (projectId) => {
    if (projectId === "__new__") {
      setNewProjectDraft({ isNew: true });
      setCreateProjectModalOpen(true);
      return;
    }
    if (projectId === "__all__") {
      setSelectedProjectId(null);
      window.localStorage.setItem("selectedProjectId", "__all__");
      const params = new URLSearchParams(location.search);
      params.delete("projectId");
      navigate({
        pathname: location.pathname,
        search: `?${params.toString()}`,
      });
      return;
    }
    setSelectedProjectId(projectId);
    window.localStorage.setItem("selectedProjectId", projectId);
    const params = new URLSearchParams(location.search);
    params.set("projectId", projectId);
    navigate({ pathname: location.pathname, search: `?${params.toString()}` });
  };

  const handleSaveNewProject = async (updatedProject) => {
    try {
      setSavingNewProject(true);
      const backendData = ProjectService.mapToBackend({
        ...updatedProject,
        isNew: true,
      });
      const response = await ProjectService.upsert(backendData);
      const created = response?.data
        ? ProjectService.mapToFrontend(response.data)
        : response;
      if (created?.id) {
        setCreateProjectModalOpen(false);
        setSelectedProjectId(created.id);
        const params = new URLSearchParams(location.search);
        params.set("projectId", created.id);
        navigate({
          pathname: location.pathname,
          search: `?${params.toString()}`,
        });
      } else {
        setCreateProjectModalOpen(false);
      }
    } catch (e) {
      setCreateProjectModalOpen(false);
    } finally {
      setSavingNewProject(false);
    }
  };

  const handleConfirm = () => {
    setOpenConfirmModal(false);
    navigate(0);
  };

  const handleConfirmUnsavedLeave = () => {
    setUnsavedModalOpen(false);
    const target = unsavedLeaveTarget;
    setUnsavedLeaveTarget(null);
    if (!target) return;
    if (target.type === "path") {
      resetState();
      navigate(target.value);
    } else if (target.type === "callback") {
      target.value();
    }
  };

  const handleCancelUnsavedLeave = () => {
    setUnsavedModalOpen(false);
    setUnsavedLeaveTarget(null);
  };

  const isBuilder = location.pathname.startsWith("/reports/builder");
  const isBuilderPreview =
    isBuilder && new URLSearchParams(location.search).get("mode") === "preview";
  useEffect(() => {
    if ((isBuilderPreview || isPreview) && unsavedModalOpen) {
      setUnsavedModalOpen(false);
      setUnsavedLeaveTarget(null);
    }
  }, [isBuilderPreview, isPreview, unsavedModalOpen]);

  return (
    <NotificationsProvider
      locationPathname={location.pathname}
      onNavigateToPath={(path) => navigate(path)}
    >
      <Layout style={{ minHeight: "100vh" }}>
        <SimpleSider
          routes={filteredRoutes}
          location={location}
          navigate={navigate}
          openKeys={openKeys}
          setOpenKeys={setOpenKeys}
          collapsed={collapsed}
          setCollapsed={setCollapsed}
        />

        <Layout>
          <Content
            style={{
              marginLeft: collapsed ? 80 : 240,
              marginTop: 0,
              minHeight: "100vh",
              backgroundColor: theme.backgroundPrimary || theme.white,
              transition: "margin-left 0.3s ease",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {showProjectTopbar ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 60px",
                  borderBottom: `1px solid ${theme.border}`,
                  background: theme.cardBackground,
                  flexShrink: 0,
                }}
              >
                <span style={{ color: theme.textSecondary, fontWeight: 600 }}>
                  Projeto:
                </span>
                <Select
                  value={selectedProjectId ?? "__all__"}
                  onChange={(value) => handleProjectChange(value)}
                  style={{ width: 320, maxWidth: "100%" }}
                  options={projectSelectOptions}
                  showSearch
                  autoClearSearchValue={false}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    String(option?.label ?? "")
                      .toLowerCase()
                      .includes(String(input ?? "").toLowerCase())
                  }
                />
              </div>
            ) : null}
            <SelectedProjectContext.Provider
              value={{ selectedProjectId, setSelectedProjectId }}
            >
              <PageLabelContext.Provider
                value={{ setPageLabel: setCustomPageLabel }}
              >
                <NavigationButtonContext.Provider value={{ setNavigationButton }}>
                  <main
                    role="main"
                    style={{
                      flex: 1,
                      padding: isBuilder ? 0 : 24,
                      overflowY: "auto",
                    }}
                  >
                    {children}
                  </main>
                </NavigationButtonContext.Provider>
              </PageLabelContext.Provider>
            </SelectedProjectContext.Provider>
          </Content>
        </Layout>

      <CustomModal
        title="Recarregar Página"
        content="Você já está nesta página. Deseja recarregar?"
        open={openConfirmModal}
        onConfirm={handleConfirm}
        confirmText="Recarregar"
        onCancel={() => setOpenConfirmModal(false)}
      />

      <CustomModal
        title="Sair sem salvar?"
        content="Você possui alterações não salvas. Tem certeza de que deseja sair e perder as alterações?"
        open={!isBuilderPreview && !isPreview && unsavedModalOpen}
        onConfirm={handleConfirmUnsavedLeave}
        onCancel={handleCancelUnsavedLeave}
        confirmText="Sair sem salvar"
        cancelText="Cancelar"
        variant="confirmation"
      />

      <CustomModal
        title={
          newProjectDraft?.isNew ? "Novo Projeto" : "Configurações do Projeto"
        }
        content={
          <ProjectSettings
            project={newProjectDraft}
            onBack={() => setCreateProjectModalOpen(false)}
            onSave={
              newProjectDraft?.isNew
                ? handleSaveNewProject
                : handleSaveProjectSettings
            }
            visible={true}
            allProjects={projects}
          />
        }
        open={createProjectModalOpen}
        onCancel={() => setCreateProjectModalOpen(false)}
        showConfirmButton={false}
        showCancelButton={true}
        variant="form"
        loading={savingNewProject}
        footer={null}
      />
      <LoginUpdateNoticeModal
        open={loginUpdateNoticeOpen}
        notice={loginUpdateNotice}
        onClose={closeLoginUpdateNotice}
        interactionId={loginUpdateNoticeInteractionId}
        onAction={(currentNotice) => {
          const action = currentNotice?.action;
          const safeUrl = getSafeExternalUrl(action?.url);
          if (!safeUrl) return;
          window.open(
            safeUrl,
            action.openInNewTab === false ? "_self" : "_blank",
            "noopener,noreferrer",
          );
        }}
      />
      <NotificationsDrawer />
      <OnboardingManager />
      <AntdTourToggle />
      </Layout>
    </NotificationsProvider>
  );
};

export default Authorized;
